import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, getMac, FALLBACKS, proxied } from "@/lib/api";
import { toast } from "sonner";
import VideoPlayer from "@/components/VideoPlayer";
import MediaCard from "@/components/MediaCard";
import {
  Home, Tv, Film, Clapperboard, Heart, Settings, MonitorPlay,
  Search, X, Loader2, Lock, ChevronLeft, PlayCircle, ChevronDown,
  ListVideo, Trash2, Plus, CheckCircle2, Calendar, RefreshCw
} from "lucide-react";

const NAV = [
  { key: "home", label: "Home", icon: Home },
  { key: "search", label: "Search", icon: Search },
  { key: "live", label: "Live TV", icon: Tv },
  { key: "movie", label: "Movies", icon: Film },
  { key: "series", label: "Series", icon: Clapperboard },
  { key: "favorites", label: "Favorites", icon: Heart },
  { key: "settings", label: "Settings", icon: Settings },
];

export default function Player() {
  const mac = getMac();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [section, setSection] = useState("home");
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [playing, setPlaying] = useState(null);
  const [favIds, setFavIds] = useState([]);
  const [episodes, setEpisodes] = useState(null);
  const [activeSeries, setActiveSeries] = useState(null);
  const [plMenu, setPlMenu] = useState(false);
  const [continueItems, setContinueItems] = useState([]);
  const [sessionPin, setSessionPin] = useState("");
  const [unlocked, setUnlocked] = useState(() => new Set());
  const [pinDialog, setPinDialog] = useState(null);

  const requestUnlock = useCallback((catName, onSuccess) => {
    if (unlocked.has(catName)) { onSuccess(); return; }
    setPinDialog({ catName, onSuccess });
  }, [unlocked]);

  const loadContinue = useCallback(() => {
    api.get(`/device/${mac}/continue`).then(({ data }) => setContinueItems(data.items)).catch(() => {});
  }, [mac]);

  const saveProgress = (item, position, duration) => {
    if (!item?.id || item.type === "live") return;
    api.post(`/device/${mac}/progress`, {
      content_id: item.id, name: item.name, logo: item.logo || "",
      type: item.type || "movie", url: item.url, position, duration,
    }).catch(() => {});
  };

  const loadDevice = useCallback(async () => {
    const { data } = await api.get(`/device/${mac}`);
    setDevice(data);
    return data;
  }, [mac]);

  useEffect(() => {
    if (!mac) { navigate("/"); return; }
    loadDevice().catch(() => navigate("/"));
    api.get(`/device/${mac}/favorites`).then(({ data }) => setFavIds(data.items.map((i) => i.id)));
    loadContinue();
  }, [mac, navigate, loadDevice, loadContinue]);

  const isGrid = ["movie", "series"].includes(section);

  const loadCats = useCallback(async (type) => {
    const { data } = await api.get(`/device/${mac}/categories`, { params: { type } });
    setCats(data);
  }, [mac]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      if (section === "favorites") {
        const { data } = await api.get(`/device/${mac}/favorites`);
        setItems(data.items);
      } else if (isGrid) {
        const { data } = await api.get(`/device/${mac}/content`, {
          params: { type: section, category: activeCat, search: search || undefined, pin: sessionPin || undefined, limit: 200 },
        });
        setItems(data.items);
      }
    } finally { setLoading(false); }
  }, [mac, section, activeCat, search, isGrid, sessionPin]);

  useEffect(() => {
    if (isGrid) { setActiveCat("all"); loadCats(section); }
  }, [section, device?.active_playlist]);

  useEffect(() => {
    const t = setTimeout(() => { if (isGrid || section === "favorites") loadItems(); }, 300);
    return () => clearTimeout(t);
  }, [loadItems]);

  const guard = () => {
    if (!device?.active) {
      toast.error("Your trial has expired. Contact the administrator to activate.");
      return false;
    }
    return true;
  };

  const play = async (item) => {
    if (!guard()) return;
    if (item.type === "series") {
      setActiveSeries(item);
      setEpisodes("loading");
      const { data } = await api.get(`/device/${mac}/series/${item.id}`);
      if (data.episodes.length === 0) {
        if (item.url) { setPlaying(item); setEpisodes(null); }
        else { toast.error("No episodes available"); setEpisodes(null); }
      } else setEpisodes(data.episodes);
      return;
    }
    setPlaying(item);
  };

  const toggleFav = async (item) => {
    const { data } = await api.post(`/device/${mac}/favorites/${item.id}`);
    setFavIds((p) => data.favorite ? [...p, item.id] : p.filter((i) => i !== item.id));
    toast.success(data.favorite ? "Added to favorites" : "Removed from favorites");
    if (section === "favorites") loadItems();
  };

  const switchPlaylist = async (pid) => {
    await api.post(`/device/${mac}/active-playlist/${pid}`);
    setPlMenu(false);
    await loadDevice();
    toast.success("Playlist switched");
    if (section === "live") setSection("home");
    else if (isGrid) loadItems();
  };

  if (!device) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-red-500 animate-spin" /></div>;

  const activePl = device.playlists?.find((p) => p.id === device.active_playlist) || device.playlists?.[0];

  return (
    <div className="flex h-screen overflow-hidden bg-[#050505]">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-[#0a0a0f] border-r border-zinc-800/50 flex flex-col">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-zinc-800/50">
          <div className="p-1.5 rounded-lg bg-red-600"><MonitorPlay className="w-4 h-4 text-white" /></div>
          <span className="font-head font-bold tracking-tight">IBO<span className="text-red-500">STREAM</span></span>
        </div>
        <nav className="flex-1 py-4 space-y-1 px-3">
          {NAV.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`nav-${key}`} onClick={() => { setSection(key); setSearch(""); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${section === key ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"}`}>
              <Icon style={{ width: 18, height: 18 }} /> {label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-800/50">
          <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${device.active ? "text-emerald-400" : "text-red-500"}`}>
            <span className={`w-2 h-2 rounded-full ${device.active ? "bg-emerald-400" : "bg-red-500"}`} />
            {device.status === "active" ? "Active" : device.active ? `Trial · ${device.days_left}d` : "Expired"}
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 shrink-0 flex items-center gap-4 px-6 border-b border-zinc-800/50 glass">
          <h1 className="font-head text-lg font-semibold">{NAV.find((n) => n.key === section)?.label}</h1>

          {/* Playlist switcher */}
          {device.playlists?.length > 0 && (
            <div className="relative">
              <button data-testid="playlist-switcher" onClick={() => setPlMenu((v) => !v)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 text-sm hover:border-red-600/50 transition-colors">
                <ListVideo className="w-4 h-4 text-red-500" />
                <span className="max-w-[140px] truncate">{activePl?.name || "Playlist"}</span>
                <ChevronDown className="w-4 h-4 text-zinc-500" />
              </button>
              {plMenu && (
                <div className="absolute z-40 mt-2 w-64 rounded-xl bg-[#121217] border border-white/10 shadow-2xl p-2" data-testid="playlist-menu">
                  {device.playlists.map((p) => (
                    <button key={p.id} data-testid={`switch-playlist-${p.id}`} onClick={() => switchPlaylist(p.id)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${p.id === device.active_playlist ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"}`}>
                      <span className="truncate">{p.name}</span>
                      {p.id === device.active_playlist && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}
                    </button>
                  ))}
                  <button onClick={() => { setPlMenu(false); setSection("settings"); }}
                    className="w-full flex items-center gap-2 px-3 py-2 mt-1 rounded-lg text-sm text-red-500 hover:bg-zinc-800/60 transition-colors">
                    <Plus className="w-4 h-4" /> Manage playlists
                  </button>
                </div>
              )}
            </div>
          )}

          {isGrid && (
            <div className="relative ml-auto w-72 max-w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..." className="w-full pl-9 pr-3 py-2 rounded-full bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/50" />
            </div>
          )}
        </header>

        <div className="flex-1 overflow-hidden">
          {section === "home" && <div className="h-full overflow-y-auto"><HomeView device={device} pl={activePl} onGo={setSection}
            continueItems={continueItems}
            onResume={(prog) => { if (!guard()) return; setPlaying({ id: prog.content_id, name: prog.name, url: prog.url, logo: prog.logo, type: prog.type, resumeFrom: prog.position }); }}
            onRemoveContinue={(cid) => { api.delete(`/device/${mac}/progress/${cid}`).then(loadContinue); }} /></div>}

          {section === "settings" && <div className="h-full overflow-y-auto"><SettingsView device={device} mac={mac} onChange={loadDevice} onAddNew={() => navigate("/")} /></div>}

          {section === "search" && <div className="h-full overflow-y-auto"><SearchView mac={mac} onPlay={play} onFav={toggleFav} favIds={favIds} /></div>}

          {section === "live" && <LiveView mac={mac} guard={guard} activePlaylist={device.active_playlist} onFav={toggleFav} favIds={favIds} requestUnlock={requestUnlock} sessionPin={sessionPin} />}

          {isGrid && (
            <div className="flex h-full">
              {cats.length > 0 && (
                <div className="w-56 shrink-0 border-r border-zinc-800/50 overflow-y-auto p-3 space-y-1">
                  <button onClick={() => setActiveCat("all")}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeCat === "all" ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white"}`}>All categories</button>
                  {cats.map((c) => (
                    <button key={c.name} data-testid={`cat-${c.name}`}
                      onClick={() => c.adult ? requestUnlock(c.name, () => setActiveCat(c.name)) : setActiveCat(c.name)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeCat === c.name ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white"}`}>
                      <span className="truncate flex items-center gap-1.5">{c.adult && <Lock className="w-3 h-3 text-amber-400 shrink-0" />}{c.name}</span>
                      <span className="shrink-0 text-xs text-zinc-500 font-mono-data">{c.count}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-6">
                <Grid loading={loading} items={items} type={section} onPlay={play} onFav={toggleFav} favIds={favIds} />
              </div>
            </div>
          )}

          {section === "favorites" && (
            <div className="h-full overflow-y-auto p-6">
              <Grid loading={loading} items={items} type="movie" onPlay={play} onFav={toggleFav} favIds={favIds} emptyText="You have no favorites" />
            </div>
          )}
        </div>
      </main>

      {/* Player overlay (movies/series) */}
      {playing && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="player-overlay">
          <div className="flex items-center gap-3 px-6 h-16 shrink-0">
            <button data-testid="close-player-btn" onClick={() => { setPlaying(null); loadContinue(); }} className="p-2 rounded-full hover:bg-zinc-800 transition-colors"><X className="w-5 h-5" /></button>
            <span className="font-head font-semibold truncate">{playing.name}</span>
          </div>
          <div className="flex-1 px-6 pb-6">
            <VideoPlayer url={playing.url} poster={playing.logo} resumeFrom={playing.resumeFrom || 0}
              onProgress={(pos, dur) => saveProgress(playing, pos, dur)} />
          </div>
        </div>
      )}

      {/* Series episodes dialog */}
      {episodes && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setEpisodes(null)}>
          <div className="bg-[#121217] border border-white/10 rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-5 border-b border-zinc-800/50">
              <ChevronLeft className="w-5 h-5 cursor-pointer" onClick={() => setEpisodes(null)} />
              <h3 className="font-head font-semibold truncate">{activeSeries?.name}</h3>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {episodes === "loading" ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-red-500 animate-spin" /></div>
              ) : episodes.map((ep) => (
                <button key={ep.id} data-testid={`episode-${ep.id}`} onClick={() => { setPlaying({ id: ep.id, name: ep.title, url: ep.url, logo: activeSeries?.logo, type: "episode" }); setEpisodes(null); }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-left transition-colors">
                  <PlayCircle className="w-5 h-5 text-red-500 shrink-0" />
                  <span className="text-sm">S{ep.season} · {ep.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {pinDialog && (
        <PinDialog
          hasPin={device.has_pin}
          mac={mac}
          onClose={() => setPinDialog(null)}
          onSuccess={(pin) => {
            setSessionPin(pin);
            setUnlocked((prev) => new Set(prev).add(pinDialog.catName));
            const cb = pinDialog.onSuccess;
            setPinDialog(null);
            loadDevice();
            cb && cb();
          }}
        />
      )}
    </div>
  );
}

/* ---------------- PIN dialog (parental control) ---------------- */
function PinDialog({ hasPin, mac, onClose, onSuccess }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    setErr("");
    if (!/^\d{4}$/.test(pin)) { setErr("PIN must be 4 digits"); return; }
    setBusy(true);
    try {
      if (hasPin) {
        await api.post(`/device/${mac}/pin/verify`, { pin });
      } else {
        if (pin !== confirm) { setErr("PINs do not match"); setBusy(false); return; }
        await api.post(`/device/${mac}/pin`, { pin });
      }
      onSuccess(pin);
    } catch (e) {
      setErr(e.response?.data?.detail === "Wrong PIN" ? "Wrong PIN" : (e.response?.data?.detail || "Error"));
    } finally { setBusy(false); }
  };

  const input = "w-full text-center tracking-[0.6em] text-2xl font-mono-data px-4 py-3 rounded-md bg-zinc-900 border border-zinc-800 focus:outline-none focus:ring-2 focus:ring-red-500/50";

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#121217] border border-white/10 rounded-2xl w-full max-w-sm p-7 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="pin-dialog">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-amber-500/20"><Lock className="w-5 h-5 text-amber-400" /></div>
          <h3 className="font-head text-lg font-semibold">{hasPin ? "Enter PIN" : "Set parental PIN"}</h3>
        </div>
        <p className="text-sm text-zinc-500">{hasPin ? "This category is locked. Enter your PIN." : "Set a 4-digit PIN to protect adult content."}</p>
        <input data-testid="pin-input" className={input} value={pin} maxLength={4} inputMode="numeric" autoFocus
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="••••"
          onKeyDown={(e) => e.key === "Enter" && hasPin && submit()} />
        {!hasPin && (
          <input data-testid="pin-confirm-input" className={input} value={confirm} maxLength={4} inputMode="numeric"
            onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))} placeholder="confirm" />
        )}
        {err && <p className="text-sm text-red-500" data-testid="pin-error">{err}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold transition-colors">Cancel</button>
          <button data-testid="pin-submit" onClick={submit} disabled={busy}
            className="flex-1 py-2.5 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white text-sm font-semibold transition-colors">
            {busy ? "..." : hasPin ? "Unlock" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Global search across TV / Movies / Series ---------------- */
function SearchView({ mac, onPlay, onFav, favIds }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/device/${mac}/search`, { params: { q } });
        setRes(data);
      } finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q, mac]);

  const sections = [
    { key: "live", label: "Live TV", type: "live" },
    { key: "movie", label: "Movies", type: "movie" },
    { key: "series", label: "Series", type: "series" },
  ];
  const totalFound = res ? (res.live.length + res.movie.length + res.series.length) : 0;

  return (
    <div className="p-6 md:p-10 fade-up">
      <div className="relative max-w-xl mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
        <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
          placeholder="Search across TV, movies and series..."
          className="w-full pl-12 pr-4 py-3.5 rounded-full bg-zinc-900 border border-zinc-800 text-base focus:outline-none focus:ring-2 focus:ring-red-500/50" />
      </div>

      {loading && <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-red-500 animate-spin" /></div>}
      {!loading && q.trim().length >= 2 && totalFound === 0 && <p className="text-zinc-500 py-10">No results for "{q}"</p>}
      {!loading && q.trim().length < 2 && <p className="text-zinc-600 py-10">Type at least 2 characters to search.</p>}

      {!loading && res && sections.map((s) => res[s.type].length > 0 && (
        <div key={s.key} className="mb-10" data-testid={`search-section-${s.key}`}>
          <h3 className="font-head text-xl font-semibold mb-4">{s.label} <span className="text-zinc-600 text-base">· {res[s.type].length}</span></h3>
          <div className={`grid gap-4 ${s.type === "live" ? "grid-cols-2 md:grid-cols-4 lg:grid-cols-5" : "grid-cols-3 md:grid-cols-5 lg:grid-cols-6"}`}>
            {res[s.type].map((it) => (
              <MediaCard key={it.id} item={it} type={it.type} onPlay={onPlay} onFav={onFav} isFav={favIds.includes(it.id)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Live TV split view with EPG ---------------- */
function LiveView({ mac, guard, activePlaylist, onFav, favIds, requestUnlock, sessionPin }) {
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [epg, setEpg] = useState(null);

  useEffect(() => {
    api.get(`/device/${mac}/categories`, { params: { type: "live" } }).then(({ data }) => setCats(data));
    setActiveCat("all");
  }, [mac, activePlaylist]);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/device/${mac}/content`, {
          params: { type: "live", category: activeCat, search: search || undefined, pin: sessionPin || undefined, limit: 300 },
        });
        setChannels(data.items);
      } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [mac, activeCat, search, activePlaylist, sessionPin]);

  const onCatChange = (name) => {
    if (name === "all") { setActiveCat("all"); return; }
    const c = cats.find((x) => x.name === name);
    if (c && c.adult) requestUnlock(name, () => setActiveCat(name));
    else setActiveCat(name);
  };

  const selectChannel = async (ch) => {
    if (!guard()) return;
    setSelected(ch);
    setEpg("loading");
    try {
      const { data } = await api.get(`/device/${mac}/epg/${ch.id}`);
      setEpg(data.epg);
    } catch { setEpg([]); }
  };

  const fmt = (s) => s ? s.split(" ")[1]?.slice(0, 5) : "";

  return (
    <div className="flex h-full">
      {/* Channel list */}
      <div className="w-[38%] max-w-md shrink-0 border-r border-zinc-800/50 flex flex-col">
        <div className="p-3 space-y-2 border-b border-zinc-800/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input data-testid="live-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search channels..." className="w-full pl-9 pr-3 py-2 rounded-full bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/50" />
          </div>
          {cats.length > 0 && (
            <select data-testid="live-category-select" value={activeCat} onChange={(e) => onCatChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/50">
              <option value="all">All categories</option>
              {cats.map((c) => <option key={c.name} value={c.name}>{`${c.adult ? "🔒 " : ""}${c.name} (${c.count})`}</option>)}
            </select>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-red-500 animate-spin" /></div>
            : channels.length === 0 ? <p className="text-zinc-500 text-center py-10 text-sm">No channels</p>
            : channels.map((ch) => (
              <button key={ch.id} data-testid={`channel-${ch.id}`} onClick={() => selectChannel(ch)}
                className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${selected?.id === ch.id ? "bg-red-600/20 border border-red-600/40" : "hover:bg-zinc-800/60 border border-transparent"}`}>
                <img src={proxied(ch.logo) || FALLBACKS.live} onError={(e) => { e.target.src = FALLBACKS.live; }}
                  className="w-12 h-9 object-contain rounded bg-[#0a0a0f] shrink-0" alt="" />
                <span className="text-sm truncate flex-1">{ch.name}</span>
                <Heart onClick={(e) => { e.stopPropagation(); onFav(ch); }}
                  className={`w-4 h-4 shrink-0 ${favIds.includes(ch.id) ? "fill-red-500 text-red-500" : "text-zinc-600 hover:text-white"}`} />
              </button>
            ))}
        </div>
      </div>

      {/* Player + EPG */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selected ? (
          <div className="flex-1 flex flex-col overflow-hidden p-5 gap-4">
            <div className="aspect-video w-full max-h-[55vh]"><VideoPlayer url={selected.url} poster={selected.logo} /></div>
            <div className="flex items-center gap-2">
              <Tv className="w-4 h-4 text-red-500" />
              <h3 className="font-head font-semibold truncate">{selected.name}</h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                <Calendar className="w-3.5 h-3.5" /> Guide (EPG)
              </div>
              {epg === "loading" ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-red-500 animate-spin" /></div>
              ) : !epg || epg.length === 0 ? (
                <p className="text-zinc-600 text-sm">No guide available for this channel.</p>
              ) : (
                <div className="space-y-2" data-testid="epg-list">
                  {epg.map((p, i) => (
                    <div key={i} className={`flex gap-3 p-3 rounded-lg ${p.now ? "bg-red-600/15 border border-red-600/40" : "bg-[#121217] border border-zinc-800/50"}`}>
                      <span className="font-mono-data text-sm text-zinc-400 shrink-0 w-24">{fmt(p.start)}–{fmt(p.end)}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.now && <span className="text-red-500 mr-2">● Now</span>}{p.title || "—"}</p>
                        {p.description && <p className="text-xs text-zinc-500 line-clamp-2 mt-0.5">{p.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-600 gap-3">
            <Tv className="w-12 h-12" />
            <p>Select a channel to watch</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Grid({ loading, items, type, onPlay, onFav, favIds, emptyText }) {
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-red-500 animate-spin" /></div>;
  if (!items.length) return <p className="text-zinc-500 text-center py-20">{emptyText || "No results"}</p>;
  return (
    <div className="grid gap-4 grid-cols-3 md:grid-cols-5 lg:grid-cols-6">
      {items.map((it) => (
        <MediaCard key={it.id} item={it} type={it.type || type} onPlay={onPlay} onFav={onFav} isFav={favIds.includes(it.id)} />
      ))}
    </div>
  );
}

function HomeView({ device, pl, onGo, continueItems = [], onResume, onRemoveContinue }) {
  const c = pl?.counts || {};
  const cards = [
    { key: "live", label: "Live TV", icon: Tv, count: c.live || 0 },
    { key: "movie", label: "Movies", icon: Film, count: c.movie || 0 },
    { key: "series", label: "Series", icon: Clapperboard, count: c.series || 0 },
  ];
  return (
    <div className="p-6 md:p-10 fade-up">
      {!device.active && (
        <div className="flex items-center gap-3 mb-6 p-4 rounded-xl bg-red-950/40 border border-red-900/50" data-testid="trial-expired-banner">
          <Lock className="w-5 h-5 text-red-500 shrink-0" />
          <p className="text-sm text-red-200">Your trial has expired. Contact the administrator to activate device <code className="font-mono-data">{device.mac}</code>.</p>
        </div>
      )}
      <h2 className="font-head text-3xl font-semibold tracking-tight">Welcome 👋</h2>
      <p className="text-zinc-400 mt-2">{pl ? `Active playlist: ${pl.name}` : "No active playlist"}</p>
      <div className="grid sm:grid-cols-3 gap-4 mt-8 max-w-3xl">
        {cards.map(({ key, label, icon: Icon, count }) => (
          <button key={key} data-testid={`home-card-${key}`} onClick={() => onGo(key)}
            className="text-left p-6 rounded-2xl bg-[#121217] border border-zinc-800/50 hover:border-red-600/50 hover:-translate-y-1 transition-transform">
            <Icon className="w-7 h-7 text-red-500" />
            <p className="font-head text-2xl font-bold mt-4">{count}</p>
            <p className="text-zinc-400 text-sm">{label}</p>
          </button>
        ))}
      </div>

      {continueItems.length > 0 && (
        <div className="mt-12" data-testid="continue-watching">
          <div className="flex items-center gap-2 mb-4">
            <PlayCircle className="w-5 h-5 text-red-500" />
            <h3 className="font-head text-xl font-semibold">Continue watching</h3>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4">
            {continueItems.map((it) => {
              const pct = it.duration > 0 ? Math.min(100, (it.position / it.duration) * 100) : 0;
              return (
                <div key={it.content_id} data-testid={`continue-${it.content_id}`} onClick={() => onResume(it)}
                  className="group relative cursor-pointer overflow-hidden rounded-lg bg-[#121217] border border-zinc-800/50 aspect-[2/3] hover:-translate-y-1 hover:border-red-600/50 transition-transform">
                  <img src={proxied(it.logo) || FALLBACKS.movie} onError={(e) => { e.target.src = FALLBACKS.movie; }} className="w-full h-full object-cover" alt="" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                  <button data-testid={`remove-continue-${it.content_id}`} onClick={(e) => { e.stopPropagation(); onRemoveContinue(it.content_id); }}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-red-600 transition-colors"><X className="w-3.5 h-3.5" /></button>
                  <div className="absolute bottom-0 left-0 right-0 p-2.5">
                    <p className="text-xs font-semibold text-zinc-100 line-clamp-2" style={{ textShadow: "0 1px 3px #000" }}>{it.name}</p>
                    <div className="mt-1.5 h-1 rounded-full bg-white/20 overflow-hidden"><div className="h-full bg-red-600" style={{ width: `${pct}%` }} /></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsView({ device, mac, onChange, onAddNew }) {
  const [busy, setBusy] = useState(null);
  const del = async (pid) => {
    await api.delete(`/device/${mac}/playlist/${pid}`);
    toast.success("Playlist deleted");
    onChange();
  };
  const setActive = async (pid) => {
    await api.post(`/device/${mac}/active-playlist/${pid}`);
    toast.success("Active playlist changed");
    onChange();
  };
  const refresh = async (pid) => {
    setBusy(pid);
    try {
      const { data } = await api.post(`/device/${mac}/playlist/${pid}/refresh`);
      toast.success(`Refreshed! TV ${data.counts.live} · Movies ${data.counts.movie} · Series ${data.counts.series}`);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error refreshing");
    } finally { setBusy(null); }
  };
  return (
    <div className="p-6 md:p-10 max-w-2xl fade-up space-y-6">
      <div className="p-6 rounded-2xl bg-[#121217] border border-zinc-800/50 space-y-3">
        <h3 className="font-head text-xl font-semibold">Device information</h3>
        <div className="flex justify-between text-sm"><span className="text-zinc-500">MAC Address</span><code className="font-mono-data text-zinc-200">{device.mac}</code></div>
        <div className="flex justify-between text-sm"><span className="text-zinc-500">Device Key</span><code className="font-mono-data text-zinc-200">{device.device_key}</code></div>
        <div className="flex justify-between text-sm"><span className="text-zinc-500">Status</span><span className={device.active ? "text-emerald-400" : "text-red-500"}>{device.status === "active" ? "Active" : device.active ? `Trial (${device.days_left} days)` : "Expired"}</span></div>
        <div className="flex justify-between text-sm"><span className="text-zinc-500">Valid until</span><span className="text-zinc-200">{new Date(device.expiry).toLocaleDateString("en-GB")}</span></div>
      </div>

      <div className="p-6 rounded-2xl bg-[#121217] border border-zinc-800/50 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-head text-xl font-semibold">Playlists ({device.playlists?.length || 0})</h3>
          <button data-testid="add-playlist-settings" onClick={onAddNew}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm font-semibold transition-colors">
            <Plus className="w-4 h-4" /> Add new
          </button>
        </div>
        {device.playlists?.length ? device.playlists.map((p) => (
          <div key={p.id} data-testid={`pl-item-${p.id}`} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900 border border-zinc-800/50">
            <ListVideo className="w-5 h-5 text-red-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{p.name} {p.id === device.active_playlist && <span className="text-emerald-400 text-xs ml-1">● Active</span>}</p>
              <p className="text-xs text-zinc-500">TV {p.counts?.live || 0} · Movies {p.counts?.movie || 0} · Series {p.counts?.series || 0} · {p.type.toUpperCase()}</p>
            </div>
            <button data-testid={`refresh-pl-${p.id}`} onClick={() => refresh(p.id)} disabled={busy === p.id} title="Refresh"
              className="p-1.5 rounded-md bg-zinc-800 hover:bg-sky-600 disabled:opacity-50 transition-colors">
              <RefreshCw className={`w-3.5 h-3.5 ${busy === p.id ? "animate-spin" : ""}`} />
            </button>
            {p.id !== device.active_playlist && (
              <button data-testid={`set-active-${p.id}`} onClick={() => setActive(p.id)}
                className="px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-emerald-600 text-xs font-semibold transition-colors">Activate</button>
            )}
            <button data-testid={`delete-pl-${p.id}`} onClick={() => del(p.id)}
              className="p-1.5 rounded-md bg-zinc-800 hover:bg-red-700 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        )) : <p className="text-zinc-500 text-sm">You have no playlists. Add one.</p>}
      </div>

      <ParentalCard device={device} mac={mac} onChange={onChange} />
    </div>
  );
}

function ParentalCard({ device, mac, onChange }) {
  const [pin, setPin] = useState("");
  const [oldPin, setOldPin] = useState("");
  const inp = "w-32 text-center tracking-[0.4em] font-mono-data px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 focus:outline-none focus:ring-2 focus:ring-red-500/50";
  const save = async () => {
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN must be 4 digits"); return; }
    try {
      await api.post(`/device/${mac}/pin`, { pin, old_pin: oldPin || undefined });
      toast.success(device.has_pin ? "PIN changed" : "PIN set");
      setPin(""); setOldPin(""); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async () => {
    try {
      await api.delete(`/device/${mac}/pin`, { data: { pin: oldPin } });
      toast.success("PIN removed"); setOldPin(""); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <div className="p-6 rounded-2xl bg-[#121217] border border-zinc-800/50 space-y-4" data-testid="parental-card">
      <div className="flex items-center gap-2.5">
        <div className="p-2 rounded-lg bg-amber-500/20"><Lock className="w-5 h-5 text-amber-400" /></div>
        <div>
          <h3 className="font-head text-xl font-semibold">Parental control</h3>
          <p className="text-xs text-zinc-500">Protect adult categories with a 4-digit PIN. {device.has_pin ? "PIN is set." : "No PIN set."}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {device.has_pin && (
          <input data-testid="old-pin-input" className={inp} value={oldPin} maxLength={4} inputMode="numeric"
            onChange={(e) => setOldPin(e.target.value.replace(/\D/g, ""))} placeholder="old PIN" />
        )}
        <input data-testid="new-pin-input" className={inp} value={pin} maxLength={4} inputMode="numeric"
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder={device.has_pin ? "new PIN" : "PIN"} />
        <button data-testid="save-pin-btn" onClick={save}
          className="px-5 py-2 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm font-semibold transition-colors">
          {device.has_pin ? "Change PIN" : "Set PIN"}
        </button>
        {device.has_pin && (
          <button data-testid="remove-pin-btn" onClick={remove}
            className="px-5 py-2 rounded-full bg-zinc-800 hover:bg-red-700 text-sm font-semibold transition-colors">Remove</button>
        )}
      </div>
    </div>
  );
}
